/**
 * Lore — Obsidian-vault-backed campaign codex.
 *
 * Read-path slice. Three panels:
 *   - Left: folder tree of every note the caller can see, grouped by the
 *     `folder` returned in the manifest. Selecting a leaf loads the note.
 *   - Centre: rendered markdown. Wiki-links and image embeds are pre-resolved
 *     by the API; we replace `[[target]]` with either an internal link or a
 *     ghosted span before handing the markdown to `marked`.
 *   - Right: a frontmatter sidebar (visibility, reveal date, summary, tags).
 *     For DMs this also exposes the persona switcher — picking a player here
 *     re-fetches the manifest with `?as={folder}` so the DM can preview the
 *     view exactly as that player sees it.
 *
 * Critically, **ghost links are visually identical** for "doesn't exist" and
 * "exists but caller can't see it". This is enforced server-side too, but
 * the client must also avoid leaking via cursor/title/data attributes.
 *
 * The page is intentionally read-only in this slice. The DM admin panel
 * (visibility checkboxes, reveal picker, draft toggle, section gating
 * toolbar) lives in a follow-up commit alongside the patch endpoint.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { marked } from 'marked';
import {
  getManifest,
  getNote,
  LoreManifestEntry,
  LoreNote,
} from '../lib/loreApi';
import { useAuth } from '../lib/AuthContext';
import { isAdmin } from '../lib/auth';
import './Lore.css';

/**
 * Folder names that the persona heuristic should NOT treat as player folders
 * when populating the preview-as dropdown. Anything not in this set and at
 * the top level of the vault is assumed to be a player and offered up. The
 * server still validates against players.json so getting this wrong is at
 * worst a confusing dropdown entry.
 */
const LoreSharedFolders = new Set<string>([
  'Lore',
  'NPCs',
  'Locations',
  'Items',
  'Sessions',
  'dnd-scribe',
  'ChatGPT Import',
  'Dixity',
  'Templates',
  'Maps',
]);

interface TreeFolder {
  name: string;
  fullPath: string;
  notes: LoreManifestEntry[];
  children: TreeFolder[];
}

/**
 * Builds a hierarchical tree from a flat manifest. The manifest's `folder`
 * field is a slash-separated path; we walk each entry into the tree, creating
 * intermediate folders as we go. Folders are sorted alphabetically; notes
 * within a folder are sorted by title.
 */
function buildTree(entries: LoreManifestEntry[]): TreeFolder {
  const root: TreeFolder = { name: '', fullPath: '', notes: [], children: [] };

  for (const entry of entries) {
    const segments = entry.folder ? entry.folder.split('/').filter(Boolean) : [];
    let cursor = root;
    let acc = '';
    for (const seg of segments) {
      acc = acc ? `${acc}/${seg}` : seg;
      let child = cursor.children.find((c) => c.name === seg);
      if (!child) {
        child = { name: seg, fullPath: acc, notes: [], children: [] };
        cursor.children.push(child);
      }
      cursor = child;
    }
    cursor.notes.push(entry);
  }

  const sortRecursive = (node: TreeFolder) => {
    node.children.sort((a, b) => a.name.localeCompare(b.name));
    node.notes.sort((a, b) => a.title.localeCompare(b.title));
    node.children.forEach(sortRecursive);
  };
  sortRecursive(root);
  return root;
}

function FolderNode({
  folder,
  selectedSlug,
  onPick,
  depth = 0,
}: {
  folder: TreeFolder;
  selectedSlug: string | null;
  onPick: (entry: LoreManifestEntry) => void;
  depth?: number;
}) {
  // Auto-expand the root and any folder that contains the selected note.
  const [open, setOpen] = useState(depth === 0);

  return (
    <div className="lore-tree__folder" style={{ paddingLeft: depth === 0 ? 0 : '0.75rem' }}>
      {folder.name && (
        <button
          type="button"
          className="lore-tree__folder-label"
          onClick={() => setOpen((o) => !o)}
        >
          <span className="lore-tree__caret">{open ? '▾' : '▸'}</span>
          {folder.name}
        </button>
      )}
      {(open || depth === 0) && (
        <>
          {folder.children.map((child) => (
            <FolderNode
              key={child.fullPath}
              folder={child}
              selectedSlug={selectedSlug}
              onPick={onPick}
              depth={depth + 1}
            />
          ))}
          <ul className="lore-tree__notes">
            {folder.notes.map((note) => (
              <li
                key={note.slug}
                className={
                  'lore-tree__note' +
                  (note.slug === selectedSlug ? ' lore-tree__note--selected' : '') +
                  (note.type === 'canvas' ? ' lore-tree__note--canvas' : '')
                }
              >
                <button type="button" onClick={() => onPick(note)}>
                  {note.title}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * Substitutes `[[target]]` and `[[target|alias]]` in the markdown with HTML
 * placeholder tags BEFORE marked sees them. Visible links become anchor tags
 * carrying the resolved slug; invisible (missing or unauthorised) links
 * become a `<span class="lore-ghost">` so they render as plain ghosted text.
 *
 * Image embeds (`![[name]]`) are passed through as `<span>` placeholders for
 * now — image streaming is a follow-up endpoint.
 *
 * Note that we look up by RAW target text from the API's links array, which
 * is the exact string the API saw inside the brackets. Two links with the
 * same target share the same lookup result.
 */
function preprocessWikiLinks(content: string, links: LoreNote['links']): string {
  // Build a map for O(1) lookup. Multiple aliases of the same target collapse
  // to the same entry, which is fine — they all share the same Visible bit.
  const linkMap = new Map<string, (typeof links)[number]>();
  for (const link of links) {
    if (!linkMap.has(link.rawTarget)) linkMap.set(link.rawTarget, link);
  }

  // Image embeds first (they need the leading `!`).
  let out = content.replace(/!\[\[([^\]]+)\]\]/g, (_, inner: string) => {
    const [target] = inner.split('|');
    return `<span class="lore-image-placeholder">📷 ${escapeHtml(target.trim())}</span>`;
  });

  out = out.replace(/\[\[([^\]]+)\]\]/g, (_, inner: string) => {
    const [target, alias] = inner.split('|').map((s) => s.trim());
    const display = alias || target;
    const link = linkMap.get(target);
    if (link && link.visible && link.resolvedSlug) {
      // Use a custom data attribute so the click handler can intercept and
      // route locally instead of doing a hard navigation.
      return `<a class="lore-link" data-lore-slug="${escapeHtml(link.resolvedSlug)}">${escapeHtml(display)}</a>`;
    }
    // Ghost: identical render whether the target doesn't exist or the caller
    // can't see it. Cursor stays default; no title attribute.
    return `<span class="lore-ghost">${escapeHtml(display)}</span>`;
  });

  return out;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export default function Lore() {
  const { user } = useAuth();
  const dm = isAdmin(user);

  const [manifest, setManifest] = useState<LoreManifestEntry[] | null>(null);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [note, setNote] = useState<LoreNote | null>(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);

  // Persona is the DM's preview-as-player choice. Empty string = real DM view.
  // For non-DM users this stays empty and the controls are hidden.
  const [persona, setPersona] = useState<string>('');

  const loadManifest = useCallback(
    async (refresh = false) => {
      setManifestError(null);
      try {
        const result = await getManifest({
          refresh,
          as: persona || undefined,
        });
        setManifest(result.entries);
      } catch (e) {
        setManifestError(e instanceof Error ? e.message : String(e));
      }
    },
    [persona],
  );

  // Reload the manifest whenever the persona changes (or on first mount).
  useEffect(() => {
    void loadManifest(false);
  }, [loadManifest]);

  // Load the selected note. Re-fires when persona changes too — the same slug
  // may have different visible content under a different persona.
  useEffect(() => {
    if (!selectedSlug) {
      setNote(null);
      return;
    }
    let cancelled = false;
    setNoteLoading(true);
    setNoteError(null);
    getNote(selectedSlug, { as: persona || undefined })
      .then((result) => {
        if (!cancelled) {
          setNote(result);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setNoteError(e instanceof Error ? e.message : String(e));
          setNote(null);
        }
      })
      .finally(() => {
        if (!cancelled) setNoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSlug, persona]);

  const tree = useMemo(() => (manifest ? buildTree(manifest) : null), [manifest]);

  // The set of unique persona folders the DM can preview as. Built from the
  // manifest under real-DM view; we cache it so switching persona doesn't
  // empty the dropdown. (Players never see this control.)
  const [knownPersonas, setKnownPersonas] = useState<string[]>([]);
  useEffect(() => {
    if (!dm || persona !== '') return; // only learn personas from the real-DM manifest
    if (!manifest) return;
    const folders = new Set<string>();
    for (const entry of manifest) {
      const top = entry.folder.split('/')[0];
      // Heuristic: a folder is a player persona if it isn't one of the known
      // shared/lore folders. The DM still picks from the dropdown, so the
      // worst-case here is showing an extra option that the server will then
      // ignore (it validates against players.json).
      if (top && !LoreSharedFolders.has(top)) folders.add(top);
    }
    setKnownPersonas([...folders].sort());
  }, [dm, persona, manifest]);

  const handlePickNote = useCallback((entry: LoreManifestEntry) => {
    if (entry.type === 'canvas') {
      // Canvas (.canvas) files are character sheets — open in /sheet eventually.
      // For now we still set the slug so the centre panel shows the placeholder.
      setSelectedSlug(entry.slug);
      return;
    }
    setSelectedSlug(entry.slug);
  }, []);

  // Intercept clicks on rendered <a class="lore-link"> tags inside the note
  // body so they navigate within this page instead of doing a hard reload.
  const handleBodyClick = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a.lore-link') as HTMLAnchorElement | null;
      if (!anchor) return;
      const slug = anchor.getAttribute('data-lore-slug');
      if (!slug) return;
      e.preventDefault();
      setSelectedSlug(slug);
    },
    [],
  );

  const renderedHtml = useMemo(() => {
    if (!note || note.type === 'canvas') return '';
    const preprocessed = preprocessWikiLinks(note.content, note.links);
    // marked v14 returns string|Promise<string>; we use the sync default.
    return marked.parse(preprocessed, { async: false }) as string;
  }, [note]);

  return (
    <div className="lore-page">
      <aside className="lore-sidebar lore-sidebar--left">
        <header className="lore-sidebar__header">
          <h2>Lore</h2>
          <button
            type="button"
            className="lore-refresh"
            title="Refresh manifest"
            onClick={() => void loadManifest(true)}
          >
            ↻
          </button>
        </header>
        {manifestError && <div className="lore-error">{manifestError}</div>}
        {!manifest && !manifestError && (
          <div className="lore-empty">Loading…</div>
        )}
        {tree && tree.children.length === 0 && tree.notes.length === 0 && (
          <div className="lore-empty">
            Nothing visible to you yet. Ask the DM to share a note.
          </div>
        )}
        {tree && (
          <FolderNode
            folder={tree}
            selectedSlug={selectedSlug}
            onPick={handlePickNote}
          />
        )}
      </aside>

      <main className="lore-main">
        {!selectedSlug && (
          <div className="lore-empty lore-empty--centre">
            Pick a note from the tree on the left.
          </div>
        )}
        {selectedSlug && noteLoading && <div className="lore-empty">Loading note…</div>}
        {selectedSlug && noteError && <div className="lore-error">{noteError}</div>}
        {note && note.type === 'canvas' && (
          <div className="lore-canvas-placeholder">
            <h3>{selectedSlug}</h3>
            <p>This is a character sheet. Open it in the game view.</p>
          </div>
        )}
        {note && note.type === 'markdown' && (
          <article
            className="lore-body"
            onClick={handleBodyClick}
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
        )}
      </main>

      <aside className="lore-sidebar lore-sidebar--right">
        {dm && (
          <section className="lore-meta lore-meta--persona">
            <h3>Preview as</h3>
            <select
              value={persona}
              onChange={(e) => {
                setPersona(e.target.value);
                // Clear the selected note: visibility may differ under the
                // new persona, and re-fetching is the safe path.
                setSelectedSlug(null);
              }}
            >
              <option value="">DM (everything)</option>
              {knownPersonas.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <p className="lore-meta__hint">
              Persona switches manifest + note fetches to <code>?as={'{folder}'}</code>.
              Server validates against players.json — unknown folders fall back to
              real DM view.
            </p>
          </section>
        )}
        {note && note.type === 'markdown' && (
          <section className="lore-meta">
            <h3>About this note</h3>
            {note.frontmatter.summary && (
              <p className="lore-meta__summary">{note.frontmatter.summary}</p>
            )}
            <dl className="lore-meta__list">
              <dt>Visibility</dt>
              <dd>{describePlayers(note.frontmatter.players)}</dd>
              {note.frontmatter.reveal && (
                <>
                  <dt>Reveal date</dt>
                  <dd>{note.frontmatter.reveal}</dd>
                </>
              )}
              {note.frontmatter.tags && note.frontmatter.tags.length > 0 && (
                <>
                  <dt>Tags</dt>
                  <dd>
                    {note.frontmatter.tags.map((t) => (
                      <span key={t} className="lore-tag">
                        {t}
                      </span>
                    ))}
                  </dd>
                </>
              )}
              <dt>ETag</dt>
              <dd className="num lore-meta__etag">{note.etag}</dd>
            </dl>
          </section>
        )}
      </aside>
    </div>
  );
}

function describePlayers(p: LoreNote['frontmatter']['players']): string {
  if (!p) return 'DM only (default)';
  if (p.all) return 'All players';
  if (p.explicitNone) return 'DM only (explicit)';
  if (p.allowed && p.allowed.length > 0) return p.allowed.join(', ');
  return 'DM only (default)';
}
